import { MAX_FILE_NAME_LENGTH, MAX_FILE_TRANSFER_BYTES } from './filexfer';

/**
 * 发送端的可恢复任务只保存元数据，不保存文件内容。
 * 用户需要重新选择同一个文件后，才会再次开始发送。
 */
export interface PersistedFileSendTask {
  version: 1;
  id: string;
  name: string;
  size: number;
  transferred: number;
  /** 文件头尾采样指纹，不保存文件内容；旧任务没有该字段时仍可兼容恢复。 */
  sourceFingerprint?: string;
  targetDeviceId?: string;
  createdAt: number;
  updatedAt: number;
  error?: string;
}

// 服务端只托管发送任务的可恢复元数据。文件本身仍只在两端之间通过
// WebRTC file channel 传输；完成或用户取消后，客户端会删除这条记录。
export type RemoteFileTransferTaskState = 'transferring' | 'failed';

export interface RemoteFileTransferTask {
  id: string;
  name: string;
  size: number;
  transferred: number;
  state: RemoteFileTransferTaskState;
  sourceFingerprint?: string;
  targetDeviceId?: string;
  createdAt: number;
  updatedAt: number;
  error?: string;
}

export const FILE_TRANSFER_TASKS_STORAGE_KEY = 'rc_file_transfer_tasks_v1';
export const FILE_TRANSFER_TASK_LIMIT = 24;
export const FILE_TRANSFER_TASK_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

function validId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9._:-]{1,128}$/.test(value);
}

function validName(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_FILE_NAME_LENGTH;
}

function validTimestamp(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) > 0;
}

function normalizeError(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  return value.replace(/[\r\n\t]+/g, ' ').trim().slice(0, 160) || undefined;
}

function validSourceFingerprint(value: unknown, size: number): value is string {
  if (typeof value !== 'string') return false;
  const match = /^v1:(\d{1,16}):([a-f0-9]{64}):([a-f0-9]{64})$/.exec(value);
  return Boolean(match && Number(match[1]) === size);
}

function normalizeTask(value: unknown, now: number): PersistedFileSendTask | null {
  if (!value || typeof value !== 'object') return null;
  const task = value as Partial<PersistedFileSendTask>;
  const size = Number(task.size);
  const transferred = Number(task.transferred);
  if (
    task.version !== 1 ||
    !validId(task.id) ||
    !validName(task.name) ||
    !Number.isSafeInteger(size) ||
    size < 0 ||
    size > MAX_FILE_TRANSFER_BYTES ||
    !Number.isSafeInteger(transferred) ||
    transferred < 0 ||
    transferred > size ||
    !validTimestamp(task.createdAt) ||
    !validTimestamp(task.updatedAt) ||
    now - task.updatedAt > FILE_TRANSFER_TASK_RETENTION_MS
  ) return null;
  const targetDeviceId = typeof task.targetDeviceId === 'string' && validId(task.targetDeviceId)
    ? task.targetDeviceId
    : undefined;
  const sourceFingerprint = validSourceFingerprint(task.sourceFingerprint, size)
    ? task.sourceFingerprint
    : undefined;
  return {
    version: 1,
    id: task.id,
    name: task.name,
    size,
    transferred,
    ...(sourceFingerprint ? { sourceFingerprint } : {}),
    ...(targetDeviceId ? { targetDeviceId } : {}),
    createdAt: task.createdAt,
    updatedAt: task.updatedAt,
    ...(normalizeError(task.error) ? { error: normalizeError(task.error) } : {}),
  };
}

export function normalizeFileTransferTasks(value: unknown, now = Date.now()): PersistedFileSendTask[] {
  if (!Array.isArray(value)) return [];
  const byId = new Map<string, PersistedFileSendTask>();
  for (const item of value) {
    const task = normalizeTask(item, now);
    if (task) byId.set(task.id, task);
  }
  return Array.from(byId.values())
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, FILE_TRANSFER_TASK_LIMIT);
}

export function loadFileTransferTasks(storage: Pick<Storage, 'getItem'>, now = Date.now()): PersistedFileSendTask[] {
  try {
    const raw = storage.getItem(FILE_TRANSFER_TASKS_STORAGE_KEY);
    return normalizeFileTransferTasks(raw ? JSON.parse(raw) : [], now);
  } catch {
    return [];
  }
}

export function saveFileTransferTasks(
  storage: Pick<Storage, 'setItem'>,
  tasks: PersistedFileSendTask[],
  now = Date.now(),
): void {
  try {
    storage.setItem(FILE_TRANSFER_TASKS_STORAGE_KEY, JSON.stringify(normalizeFileTransferTasks(tasks, now)));
  } catch {
    // 隐私模式或浏览器存储不可用时，当前页面内的重试仍然有效。
  }
}

export function upsertFileTransferTask(
  tasks: PersistedFileSendTask[],
  task: Omit<PersistedFileSendTask, 'version' | 'createdAt' | 'updatedAt'> & {
    createdAt?: number;
    updatedAt?: number;
  },
  now = Date.now(),
): PersistedFileSendTask[] {
  const previous = tasks.find((item) => item.id === task.id);
  const next: PersistedFileSendTask = {
    version: 1,
    id: task.id,
    name: task.name,
    size: task.size,
    transferred: task.transferred,
    ...(task.sourceFingerprint
      ? { sourceFingerprint: task.sourceFingerprint }
      : previous?.sourceFingerprint
        ? { sourceFingerprint: previous.sourceFingerprint }
        : {}),
    ...(task.targetDeviceId ? { targetDeviceId: task.targetDeviceId } : previous?.targetDeviceId ? { targetDeviceId: previous.targetDeviceId } : {}),
    createdAt: task.createdAt || previous?.createdAt || now,
    updatedAt: task.updatedAt || now,
    ...(task.error ? { error: task.error } : {}),
  };
  return normalizeFileTransferTasks([
    ...tasks.filter((item) => item.id !== task.id),
    next,
  ], now);
}

export function removeFileTransferTask(tasks: PersistedFileSendTask[], id: string): PersistedFileSendTask[] {
  return tasks.filter((task) => task.id !== id);
}
